import React from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import { ColumnFlexBox, CenteredFlexBox } from '../../FlexBox';
import useTranslate from '../../../Hooks/useTranslate';
import CellSelect from './CellSelect';
import { filtersForType } from '../filters/FilterRegistry';
import {
  entryKindLabel,
  entryUsesCell,
  entryUsesLabel,
} from '../detail/EntryRegistry';

/**
 * The entry's identity and the buttons every row carries, whatever the surface.
 *
 * `stacked` puts the controls under the name instead of beside it, for the column a sidebar has
 * rather than the width a dialog has.
 *
 * Every list of entries is ordered by dragging its lines, so each takes a grip and none takes the
 * pair of arrows that used to move one place at a time.
 */
const EntryFrame = function EntryFrame({
  entry,
  index,
  stacked,
  onOpen,
  onRemove,
  children,
}) {
  const translate = useTranslate();

  const identity = (
    <ColumnFlexBox
      onClick={onOpen ? () => onOpen(index) : undefined}
      sx={{ flex: 1, minWidth: 0, cursor: onOpen ? 'pointer' : 'inherit' }}
    >
      <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
        {/* An entry that draws no value has no wording either, so it is listed by what it is. */}
        {translate(
          entry.label ?? (entry.kind ? entryKindLabel(entry.kind) : entry.key)
        )}
      </Typography>
      <Typography variant="caption" noWrap sx={{ opacity: '60%' }}>
        {entry.key}
      </Typography>
    </ColumnFlexBox>
  );

  const handle = (
    <DragIndicatorRoundedIcon
      fontSize="small"
      sx={{ opacity: 0.5, flex: 'none' }}
    />
  );

  const buttons = (
    <IconButton
      id={`remove-${entry.key}`}
      size="small"
      onClick={() => onRemove(index)}
    >
      <DeleteRoundedIcon fontSize="small" />
    </IconButton>
  );

  if (stacked)
    return (
      <ColumnFlexBox
        gap="8px"
        sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: '8px' }}
      >
        <CenteredFlexBox gap="4px">
          {handle}
          {identity}
          {buttons}
        </CenteredFlexBox>
        <CenteredFlexBox gap="8px" sx={{ flexWrap: 'wrap' }}>
          {children}
        </CenteredFlexBox>
      </ColumnFlexBox>
    );

  return (
    <CenteredFlexBox
      gap="8px"
      sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: '8px' }}
    >
      {handle}
      {identity}
      {children}
      {buttons}
    </CenteredFlexBox>
  );
};

/**
 * An entry as a line of a list: what it is, and whether it stays.
 *
 * What it holds is not shown - it is opened for that - so a column the width of a sidebar can list
 * a section's worth of keys rather than three of them.
 */
export const EntryListRow = React.memo(function EntryListRow({
  entry,
  index,
  onOpen = () => {},
  onRemove = () => {},
}) {
  return (
    <EntryFrame
      entry={entry}
      index={index}
      onOpen={onOpen}
      onRemove={onRemove}
    />
  );
});

/**
 * How many of the grid's twelve columns the entry takes from a breakpoint up, held as a list
 * column's `size` is. `fullRow` makes it take the whole row below that breakpoint.
 */
const WidthField = function WidthField({
  entry,
  index,
  onChange,
  fullRow = false,
  breakpoint = 'md',
  min = 0,
  step = 0.1,
  width = '80px',
}) {
  const intl = useIntl();

  return (
    <TextField
      size="small"
      type="number"
      label={intl.messages.WIDTH}
      value={entry.size?.[breakpoint] ?? ''}
      onChange={(e) =>
        onChange(index, {
          size: e.target.value
            ? {
                ...(fullRow ? { xs: 12 } : {}),
                [breakpoint]: Number(e.target.value),
              }
            : undefined,
        })
      }
      sx={{ width, flex: 'none' }}
      slotProps={{ htmlInput: { min, max: 12, step } }}
    />
  );
};

/** Everything the entry carries beyond its identity, which is a page of its own. */
const ConfigureButton = function ConfigureButton({ summary, onClick }) {
  const translate = useTranslate();

  return (
    <Tooltip arrow title={summary}>
      <span>
        <Button
          size="small"
          startIcon={<TuneRoundedIcon />}
          onClick={onClick}
          sx={{
            flex: 'none',
            textTransform: 'none',
            width: '150px',
            fontWeight: summary ? 600 : 400,
          }}
        >
          {translate('CONFIGURE_ENTRY')}
        </Button>
      </span>
    </Tooltip>
  );
};

/**
 * What the surface calls the entry, where the key itself would not do.
 *
 * `width` is the dialog's, which has room for it beside the rest of the row; a sidebar does not, so
 * the detail row asks for the whole of its own line.
 */
const LabelField = function LabelField({
  entry,
  index,
  onChange,
  width = '260px',
}) {
  const intl = useIntl();

  return (
    <TextField
      size="small"
      label={intl.messages.LABEL}
      value={entry.label ?? ''}
      onChange={(e) => onChange(index, { label: e.target.value })}
      sx={{ width, flex: 'none' }}
    />
  );
};

/** What an entry already carries, as the configure button's tooltip. */
const configuredSummary = (entry, messages) =>
  [
    entry.options?.length > 0 &&
      `${messages.VALUE_MAP}: ${entry.options.length}`,
    entry.caption?.key && `${messages.SECOND_LINE}: ${entry.caption.key}`,
    entry.optionsUrl && `${messages.DATA_SOURCE}: ${entry.optionsUrl}`,
    (entry.maxParameter || entry.max !== undefined) &&
      `${messages.SCALE}: ${entry.minParameter ?? entry.min ?? 0} – ${
        entry.maxParameter ?? entry.max
      }`,
  ]
    .filter(Boolean)
    .join(' — ');

/**
 * Which one of each collection a key reaches through it means - one control per collection, since
 * a key can cross more than one and each is chosen separately.
 *
 * The brackets a key arrives with are empty, and read the first of them until told better. What
 * they can be told is either a parameter the page publishes, which follows it as it changes, or a
 * rule the wanted one answers to, which is written into the key and shown as it stands.
 */
const IndexField = function IndexField({ entry, index, parameters, onChange }) {
  const intl = useIntl();

  const crossings = [...(entry.key ?? '').matchAll(/\[([^\]]*)\]/g)].map(
    ([, chooses]) => chooses
  );

  /** The key with the nth of its brackets holding something else. */
  const rewritten = (at, chooses) => {
    let seen = -1;

    return entry.key.replace(/\[[^\]]*\]/g, (bracket) =>
      (seen += 1) === at ? `[${chooses}]` : bracket
    );
  };

  return crossings.map((chooses, at) => (
    <Select
      // A key crosses the collections it crosses; which one this is, is where it sits in the key.
      key={at}
      size="small"
      value={chooses}
      displayEmpty
      onChange={(e) => onChange(index, { key: rewritten(at, e.target.value) })}
      sx={{ width: '170px', flex: 'none' }}
    >
      <MenuItem value="">{intl.messages.FIRST_OF_THEM}</MenuItem>
      {parameters.map((parameter) => (
        <MenuItem key={parameter} value={`$${parameter}`}>
          {parameter}
        </MenuItem>
      ))}
      {/* A rule is written rather than chosen, so it is offered back as what it is. */}
      {chooses && !parameters.includes(chooses.replace('$', '')) && (
        <MenuItem value={chooses}>{chooses}</MenuItem>
      )}
    </Select>
  ));
};

/**
 * An entry of a detail region: what draws it, the share of the row it takes, and how it is set out.
 *
 * A list column and a detail field are the same entry and are drawn by the same cells, but they do
 * not sit the same way: a column has a place on a card and a caption it can drop, a field has a
 * share of a row, a caption above or beside it, and the weight of a figure or of a property. An
 * entry that draws no value - a rule, a heading, a gap - is offered only what it has.
 */
export const FieldEntryRow = React.memo(function FieldEntryRow({
  entry,
  index,
  type,
  titled = false,
  parameters = [],
  onChange = () => {},
  onRemove = () => {},
  onConfigure = () => {},
}) {
  const intl = useIntl();

  const drawsValue = entryUsesCell(entry.kind);

  return (
    <EntryFrame entry={entry} index={index} stacked onRemove={onRemove}>
      {drawsValue && (
        <CellSelect
          value={entry.cell}
          type={type}
          onChange={(cell) => onChange(index, { cell })}
        />
      )}
      {/* An entry whose wording is its whole content - a heading - is named here rather than only
          behind the configure dialog: there is no key to read it from and nothing else to type. */}
      {entryUsesLabel(entry.kind) && (
        <LabelField
          entry={entry}
          index={index}
          onChange={onChange}
          width="100%"
        />
      )}
      {/* A key reaching through a collection addresses many values until it is told which one. */}
      {entry.key?.includes('[') && (
        <IndexField
          entry={entry}
          index={index}
          parameters={parameters}
          onChange={onChange}
        />
      )}
      <WidthField
        entry={entry}
        index={index}
        onChange={onChange}
        fullRow
        breakpoint="sm"
        min={1}
        step={1}
        width="96px"
      />
      {/* Where the widget has a heading, it is one of these keys rather than a setting beside them:
          what the card is called is one of the things it shows. */}
      {titled && drawsValue && (
        <Tooltip arrow title={intl.messages.USE_AS_TITLE}>
          <IconButton
            id={`title-${entry.key}`}
            size="small"
            color={entry.isTitle ? 'primary' : 'default'}
            onClick={() =>
              onChange(index, { isTitle: !entry.isTitle || undefined })
            }
          >
            {entry.isTitle ? <StarRoundedIcon /> : <StarBorderRoundedIcon />}
          </IconButton>
        </Tooltip>
      )}
      <ConfigureButton
        summary={configuredSummary(entry, intl.messages)}
        onClick={() => onConfigure(index)}
      />
    </EntryFrame>
  );
});

/** A list column: the cell that renders it, how wide, and what that cell reads. */
export const ColumnEntryRow = React.memo(function ColumnEntryRow({
  entry,
  index,
  type,
  onChange = () => {},
  onRemove = () => {},
  onConfigure = () => {},
}) {
  const intl = useIntl();

  return (
    <EntryFrame entry={entry} index={index} onRemove={onRemove}>
      <CellSelect
        value={entry.cell}
        type={type}
        onChange={(cell) => onChange(index, { cell })}
      />
      <WidthField entry={entry} index={index} onChange={onChange} />
      <ConfigureButton
        summary={configuredSummary(entry, intl.messages)}
        onClick={() => onConfigure(index)}
      />
    </EntryFrame>
  );
});

/** A filter: the field that collects it, how wide, and how the value is matched. */
export const FilterEntryRow = React.memo(function FilterEntryRow({
  entry,
  index,
  type,
  onChange = () => {},
  onRemove = () => {},
  onConfigure = () => {},
}) {
  const translate = useTranslate();
  const intl = useIntl();

  return (
    <EntryFrame entry={entry} index={index} onRemove={onRemove}>
      <Select
        size="small"
        value={entry.type ?? 'text'}
        onChange={(e) => onChange(index, { type: e.target.value })}
        sx={{ width: '150px', flex: 'none' }}
      >
        {filtersForType(type).map(({ type: value, label }) => (
          <MenuItem key={value} value={value}>
            {translate(label)}
          </MenuItem>
        ))}
      </Select>
      <WidthField entry={entry} index={index} onChange={onChange} fullRow />
      <ConfigureButton
        summary={configuredSummary(entry, intl.messages)}
        onClick={() => onConfigure(index)}
      />
    </EntryFrame>
  );
});

/** A sortable column: what the sort menu calls it, and whether it is the one applied by default. */
export const SortEntryRow = React.memo(function SortEntryRow({
  entry,
  index,
  onChange = () => {},
  onRemove = () => {},
}) {
  const intl = useIntl();

  return (
    <EntryFrame entry={entry} index={index} onRemove={onRemove}>
      <LabelField entry={entry} index={index} onChange={onChange} />
      <Tooltip arrow title={intl.messages.DEFAULT_SORT}>
        <IconButton
          id={`default-sort-${entry.key}`}
          size="small"
          color={entry.isDefault ? 'primary' : 'default'}
          onClick={() =>
            onChange(index, { isDefault: !entry.isDefault || undefined })
          }
        >
          {entry.isDefault ? <StarRoundedIcon /> : <StarBorderRoundedIcon />}
        </IconButton>
      </Tooltip>
    </EntryFrame>
  );
});

/** A key the search box matches typed text against; it carries nothing to configure. */
export const SearchEntryRow = React.memo(function SearchEntryRow({
  entry,
  index,
  onChange = () => {},
  onRemove = () => {},
}) {
  return (
    <EntryFrame entry={entry} index={index} onRemove={onRemove}>
      <LabelField entry={entry} index={index} onChange={onChange} />
    </EntryFrame>
  );
});

/** The row a surface's entries are edited through. */
export const ROW_BY_SURFACE = {
  list: ColumnEntryRow,
  filters: FilterEntryRow,
  sorting: SortEntryRow,
  search: SearchEntryRow,
};
